<div class="flex flex-col gap-4">
  <div class="flex items-center justify-between">
    <h2 class="text-xl font-medium leading-8 tracking-wide text-gray-900 m-0">
      Secret Sources
    </h2>
    <button mat-button (click)="onAddNew()" class="add-button">
      <mat-icon>add_circle</mat-icon>
      <span>Add New</span>
    </button>
  </div>

  <!-- Action error (e.g. a failed delete) -->
  @if (actionError()) {
    <div class="error-message">
      <mat-icon>error</mat-icon>
      <span>{{ actionError() }}</span>
    </div>
  }

  <!-- Inline Add Form -->
  @if (showAddForm()) {
    <div class="inline-add-form">
      <div class="form-header">
        <h3>New Secret Source</h3>
        <div class="form-actions">
          <button
            mat-button
            (click)="onCancelAdd()"
            [disabled]="addFormLoading()"
            class="cancel-button"
          >
            <span>Cancel</span>
          </button>
          <button
            mat-button
            (click)="onSaveAdd()"
            [disabled]="addFormLoading() || addForm.invalid"
            class="add-button"
          >
            @if (addFormLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
            }
            @if (!addFormLoading()) {
              <mat-icon>add</mat-icon>
            }
            @if (!addFormLoading()) {
              <span>Add</span>
            }
          </button>
        </div>
      </div>
      <form [formGroup]="addForm" (ngSubmit)="onSaveAdd()">
        <!-- Error Message -->
        @if (addFormError()) {
          <div class="add-form-error">
            <mat-icon>error</mat-icon>
            <span>{{ addFormError() }}</span>
          </div>
        }
        <!-- Form Grid Layout - 2x2 -->
        <div class="form-grid">
          <!-- Row 1: Source Name and Type -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Source Name</mat-label>
              <input matInput formControlName="name" placeholder="Vault-1" />
            </mat-form-field>
            @if (getAddFormFieldError("name")) {
              <div class="validation-error">
                {{ getAddFormFieldError("name") }}
              </div>
            }
          </div>
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Type</mat-label>
              <mat-select
                formControlName="sourceType"
                (selectionChange)="onSourceTypeChange()"
              >
                @for (type of sourceTypes(); track type.value) {
                  <mat-option [value]="type.value">
                    {{ type.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (getAddFormFieldError("sourceType")) {
              <div class="validation-error">
                {{ getAddFormFieldError("sourceType") }}
              </div>
            }
          </div>
          <!-- Row 2: type-specific config fields -->
          @if (isVaultType) {
            <div class="field-container">
              <div class="text-xs text-gray-500 mt-1">
                The Vault address and token are configured on the server. Just
                name the source; add secrets with their Vault paths afterward.
              </div>
            </div>
          }
          @if (isGcpType) {
            <div class="field-container" formGroupName="config">
              <mat-form-field appearance="fill">
                <mat-label>GCP Project ID*</mat-label>
                <input
                  matInput
                  formControlName="projectId"
                  placeholder="my-gcp-project"
                />
                <mat-hint
                  >Short secret ids resolve against this project. Values are
                  fetched server-side via ambient credentials — no token to
                  store.</mat-hint
                >
              </mat-form-field>
              @if (getAddFormFieldError("config.projectId")) {
                <div class="validation-error">
                  {{ getAddFormFieldError("config.projectId") }}
                </div>
              }
            </div>
          }
        </div>
      </form>
    </div>
  }

  <!-- Inline Edit Form -->
  @if (showEditForm()) {
    <div class="inline-edit-form">
      <div class="form-header">
        <h3>Edit Secret Source</h3>
        <div class="form-actions">
          <button
            mat-button
            (click)="onCancelEdit()"
            [disabled]="editFormLoading()"
            class="cancel-button"
          >
            <span>Cancel</span>
          </button>
          <button
            mat-button
            (click)="onSaveEdit()"
            [disabled]="editFormLoading() || editForm.invalid"
            class="save-button"
          >
            @if (editFormLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
            }
            @if (!editFormLoading()) {
              <mat-icon>save</mat-icon>
            }
            @if (!editFormLoading()) {
              <span>Save</span>
            }
          </button>
        </div>
      </div>
      <form [formGroup]="editForm" (ngSubmit)="onSaveEdit()">
        <!-- Error Message -->
        @if (editFormError()) {
          <div class="edit-form-error">
            <mat-icon>error</mat-icon>
            <span>{{ editFormError() }}</span>
          </div>
        }
        <!-- Form Grid Layout - 2x2 -->
        <div class="form-grid">
          <!-- Row 1: Source Name and Type -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Source Name</mat-label>
              <input matInput formControlName="name" placeholder="Vault-1" />
            </mat-form-field>
            @if (getEditFormFieldError("name")) {
              <div class="validation-error">
                {{ getEditFormFieldError("name") }}
              </div>
            }
          </div>
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Type</mat-label>
              <mat-select formControlName="sourceType">
                @for (type of sourceTypes(); track type.value) {
                  <mat-option [value]="type.value">
                    {{ type.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <div class="text-xs text-gray-500 mt-1">
              Source type cannot be changed after creation.
            </div>
          </div>
          <!-- Row 2: type-specific config fields -->
          @if (isEditVaultType) {
            <div class="field-container">
              <div class="text-xs text-gray-500 mt-1">
                The Vault address and token are configured on the server; there
                is nothing to edit here.
              </div>
            </div>
          }
          @if (isEditGcpType) {
            <div class="field-container" formGroupName="config">
              <mat-form-field appearance="fill">
                <mat-label>GCP Project ID*</mat-label>
                <input
                  matInput
                  formControlName="projectId"
                  placeholder="my-gcp-project"
                />
                <mat-hint
                  >Short secret ids resolve against this project.</mat-hint
                >
              </mat-form-field>
              @if (getEditFormFieldError("config.projectId")) {
                <div class="validation-error">
                  {{ getEditFormFieldError("config.projectId") }}
                </div>
              }
            </div>
          }
        </div>
      </form>
    </div>
  }

  <mat-card
    class="border border-blue-100 rounded-lg shadow-sm overflow-hidden p-0"
  >
    @if (loading()) {
      <div class="flex justify-center items-center py-10">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    }

    <!-- Empty State -->
    @if (!loading() && dataSource.data.length === 0) {
      <div class="flex flex-col justify-center items-center py-16 gap-4">
        <mat-icon class="text-gray-400 text-6xl">source</mat-icon>
        <h3 class="text-gray-600 text-lg font-medium m-0">
          No secret sources found
        </h3>
        <p class="text-gray-500 text-sm m-0 text-center max-w-sm">
          Configure secret sources like Vault to securely manage your secrets.
        </p>
      </div>
    }

    @if (!loading() && dataSource.data.length > 0) {
      <div>
        <table mat-table [dataSource]="dataSource" matSort class="w-full">
          <!-- Source Name Column -->
          <ng-container matColumnDef="name">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="table-header"
            >
              Source Name
            </th>
            <td mat-cell *matCellDef="let element" class="table-cell">
              {{ element.name }}
            </td>
          </ng-container>
          <!-- Type Column -->
          <ng-container matColumnDef="sourceType">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              class="table-header"
            >
              Type
            </th>
            <td mat-cell *matCellDef="let element" class="table-cell">
              <div
                class="source-type-badge"
                [class.default-type]="
                  element.sourceType === SECRET_SOURCE_TYPES.DEFAULT
                "
                [class.vault-type]="
                  element.sourceType === SECRET_SOURCE_TYPES.VAULT
                "
                [class.gcp-type]="
                  element.sourceType === SECRET_SOURCE_TYPES.GCP_SECRET_MANAGER
                "
              >
                <mat-icon>{{
                  sourceTypeDisplay(element.sourceType).icon
                }}</mat-icon>
                <span>{{ sourceTypeDisplay(element.sourceType).label }}</span>
              </div>
            </td>
          </ng-container>
          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="table-header-center">
              Actions
            </th>
            <td mat-cell *matCellDef="let element" class="table-cell-center">
              <button
                mat-icon-button
                [matMenuTriggerFor]="secretSourceMenu"
                class="action-menu-button"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #secretSourceMenu="matMenu">
                <button mat-menu-item (click)="onActionMenu(element, 'edit')">
                  <mat-icon>edit</mat-icon>
                  <span>Edit</span>
                </button>
                <button mat-menu-item (click)="onActionMenu(element, 'delete')">
                  <mat-icon>delete</mat-icon>
                  <span>Remove</span>
                </button>
              </mat-menu>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="data-row"
          ></tr>
        </table>
      </div>
    }
  </mat-card>
</div>
